import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import { ApiResponse } from '../types/api';
import { AccountResponse } from '../types/account';

const API_BASE = process.env.REACT_APP_API_URL;

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add auth token interceptor
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Convert axios response to our ApiResponse format
const handleResponse = async <T>(promise: Promise<AxiosResponse>): Promise<ApiResponse<T>> => {
  try {
    console.log('🚀 Starting API request...');
    const response = await promise.catch(error => {
      console.log('🔥 Promise rejected:', error);
      throw error;
    });
    console.log('✅ API response received:', response);
    
    // Check if the response has data property
    if (!response.data) {
      console.log('⚠️ No data in response');
      return {
        success: false,
        error: 'No data received from server'
      };
    }

    // If the response data has an error property, treat it as an error
    if (response.data.error) {
      console.log('⚠️ Error in response data:', response.data.error);
      return {
        success: false,
        error: response.data.error,
        data: response.data
      };
    }

    console.log('✨ Successful response:', response.data);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.log('❌ API error caught:', error);
    
    if (axios.isAxiosError(error)) {
      if (error.response) {
        console.log('🔍 Axios error details:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
        
        // Return a properly structured error response
        return {
          success: false,
          error: error.response.data?.error || 
                 error.response.data?.message || 
                 `Request failed with status ${error.response.status}`,
          status: error.response.status,
          data: error.response.data
        };
      }
      
      console.log('🌐 Network error:', error.message);
      return {
        success: false,
        error: 'Network error occurred',
        status: 0
      };
    }
    
    console.error('💥 Unexpected error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unknown error occurred',
      status: 500
    };
  }
};

// Update the post method to include timeout
const post = <T>(endpoint: string, data?: any, config?: AxiosRequestConfig) => {
  console.log(`📡 Making POST request to ${endpoint}`, { 
    endpoint,
    data,
    baseURL: axiosInstance.defaults.baseURL,
    headers: axiosInstance.defaults.headers
  });
  
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error('Request timeout after 10 seconds'));
    }, 10000);
  });

  return Promise.race([
    handleResponse<T>(
      axiosInstance.post(endpoint, data, { 
        ...config,
        timeout: 10000 // 10 second timeout
      })
      .catch(error => {
        console.log('🚨 Axios post error caught:', error);
        throw error;
      })
    ),
    timeoutPromise
  ]) as Promise<ApiResponse<T>>;
};

export const api = {
  get: <T>(endpoint: string, config?: AxiosRequestConfig) =>
    handleResponse<T>(axiosInstance.get(endpoint, config)),

  patch: <T>(endpoint: string, data?: any, config?: AxiosRequestConfig) =>
    handleResponse<T>(axiosInstance.patch(endpoint, data, config)),

  post,  // Use the updated post function

  put: <T>(endpoint: string, data?: any, config?: AxiosRequestConfig) =>
    handleResponse<T>(axiosInstance.put(endpoint, data, config)),

  delete: <T>(endpoint: string, config?: AxiosRequestConfig) =>
    handleResponse<T>(axiosInstance.delete(endpoint, config)),
};

// Account-specific endpoints
export const getAccountInfo = async (): Promise<AccountResponse> => {
  try {
    const response = await api.get<AccountResponse>('/api/account/info');
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch account info');
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching account info:', error);
    throw error;
  }
};

export default api;
